<template>
    <NonSkuStoneSearch
        :active-filter-count
        :bounds="boundsInternal"
        :category="StoneCategoryCode.Gemstone"
        :errors
        :fetching
        :filter-chips
        :get-sort-route
        :response
        :route-reset
        :shape="filters?.shape || undefined"
        :sorting
        :sorting-options
        :stale
        :stones
    >
        <template #before-filters>
            <CmsBlocks :data="data?.pageProductListing?.beforeFilters || []" />
        </template>

        <template #filters-quick>
            <ShapeFilter
                v-if="shapes"
                v-qa="'filter-shape'"
                :get-route="getRouteShape"
                :locked-to="ringShape"
                :selected-shape="filters?.shape || undefined"
                :shapes
            />
        </template>

        <template #preview="{ stone, tableView }">
            <StonePreview
                :data-qa-stone-props="
                    JSON.stringify({
                        id: stone.id,
                    })
                "
                :stone
                :stone-route="getStoneRoute(stone.id)"
                :table-view
            />
        </template>

        <template #after-grid>
            <CmsBlocks :data="data?.pageProductListing?.afterGrid || []" />
        </template>

        <template #filters-chips>
            <FilterChips
                v-qa="'filter-chips-filter'"
                :chips="filterChips"
                :route-reset
            />
        </template>

        <template #filters>
            <GemstoneFilters
                v-if="boundsInternal"
                v-model="filters"
                :bounds="boundsInternal"
            >
                <template #stone-type>
                    <StoneSearchTabs
                        :shape="
                            gemsApiShapeToShapeCode(filters?.shape || undefined)
                        "
                    />
                </template>

                <template #price>
                    <FilterPriceRange
                        v-if="boundsInternal && boundsInternal.price"
                        v-model="priceFilterModel"
                        v-qa="'filter-price'"
                        :bounds="boundsInternal.price"
                    />
                </template>
            </GemstoneFilters>
        </template>

        <template #no-results-found>
            <ThTitle
                alignment="CENTER"
                color="var(--blue)"
                size="SMALL"
                tag="H3"
            >
                We couldn't find any gemstones matching your current search
                criteria.
            </ThTitle>

            <ThSpacer size="SMALL" />

            <ThText
                alignment="CENTER"
                color="var(--slate)"
                size="MEDIUM"
            >
                <p>
                    There are natural diamonds available for this shape –
                    <ThLink
                        :to="
                            routes.diamondSearch(GemsApi_StoneCategory.Diamond)
                        "
                    >
                        return to the natural diamond search.
                    </ThLink>
                </p>

                <p>
                    Please
                    <ThLink @click="openSpeakToExpertModal">contact us</ThLink>
                    if you have a specific diamond search request and we can
                    help you with your enquiry.
                </p>
            </ThText>
        </template>

        <template #stone-modal>
            <GemstoneModal
                :data-qa="'gemstone-modal'"
                :stone="modalStone"
                @close="closeStoneModal"
                @select="selectStone"
            />
        </template>
    </NonSkuStoneSearch>
</template>

<script lang="ts" setup>
import { computed, ref, type Ref, toRef, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import {
    GemsApi_StoneCategory,
    type NonSkuGemstoneBoundsFragment,
    type NonSkuGemstoneFilters,
    type NonSkuGemstoneFragment,
    NonSkuGemstoneSortField,
    type NonSkuGemstoneSortInput,
    NonSkuSortDirection,
    StoneCategoryCode,
    type StoneShapeCode,
    useGemstoneSearchQuery,
} from '@/graphql/api'

import { useRegionPlugin } from '@/plugins/region'
import { useTranslator } from '@/i18n'
import { useRoutes } from '@/router/routes'
import {
    type DecodedGemstoneFiltersAndIdentifier,
    decodeNonSkuGemstoneFiltersAndSort,
    getGemstoneTypeName,
} from '@/domain/stone/search/nonsku/gemstone'
import { useRouterNavigation } from '@/plugins/released-urls/useRouterNavigation'
import { useRingBuilder } from '@/composables/useRingBuilder'
import { useAllowedShapes } from '@/domain/stone/search/nonsku/shape'
import { useResponseError } from '@/helpers/errors'
import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'
import { useActivated } from '@/composables/useActivated'
import { useMeta } from '@/composables/app/useMeta'
import { useFiltersCount } from '@/views/product/useFiltersCount'
import type { DropdownOption } from '@/components/ui/SortingDropdown.vue'
import { useSpeakToExpertModal } from '@/composables/app/useSpeakToExpertModal'
import { useRingShapeConstraintForNonSKU } from '@/domain/stone/search/useOrderedShapeFragments'
import type { FilterChip } from '@/components/ui/filter/FilterChips.vue'
import FilterChips from '@/components/ui/filter/FilterChips.vue'
import { getClearFilter } from '@/helpers/filter'
import { formatNumericProperty } from '@/domain/stone/labels'
import { useFormattedPriceRange } from '@/composables/useMoneyFormatter'

import NonSkuStoneSearch from '@/components/stone/search/nonsku/NonSkuStoneSearch.vue'
import CmsBlocks from '@/components/cms/CmsBlocks.vue'
import StonePreview from '@/components/stone/StonePreview.vue'
import FilterPriceRange from '@/components/stone/search/nonsku/filter/FilterPriceRange.vue'
import ShapeFilter from '@/components/stone/search/ShapeFilter.vue'
import GemstoneFilters from '@/components/stone/search/nonsku/GemstoneFilters.vue'
import GemstoneModal from '@/components/stone/search/nonsku/modal/GemstoneModal.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ThSpacer from '@/components/ui/ThSpacer.vue'
import ThTitle from '@/components/ui/title/ThTitle.vue'
import ThText from '@/components/ui/ThText.vue'
import { systemCodeToWords } from '@/helpers'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import {
    useObjectComputed,
    useObjectWritableComputed,
} from '@/composables/useObjectComputed.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import StoneSearchTabs from '@/components/stone/search/StoneSearchTabs.vue'
import { gemsApiShapeToShapeCode } from '@/domain/stone/search/sku/shape'

const router = useRouter()
const routes = useRoutes()

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const currency = useCurrencyInput()
const { t } = useI18n()
const redirect = useRedirect()

const variables = computed(() => ({
    filters: filters.value || {},
    sort: sorting.value,
    currency: currency.value,
    region: regionCode.value,
    locale: locale.value,
    contentPath: '/non-sku-gemstones',
}))

const { open: openSpeakToExpertModal } = useSpeakToExpertModal()

const hashParams = computed(
    (): DecodedGemstoneFiltersAndIdentifier | undefined =>
        decodeNonSkuGemstoneFiltersAndSort(
            router.currentRoute.value.hash.substring(1)
        )
)

const stoneId = computed(() => hashParams.value?.stoneId)

const filters = useObjectWritableComputed({
    get: (): Required<NonSkuGemstoneFilters> => ({
        caratWeightRange: null,
        priceRange: null,
        shape: null,
        types: null,
        ...hashParams.value?.filters,
    }),
    set: (filters) => {
        router.push(
            routes.nonSku.gemstoneSearch(filters, sorting.value, stoneId.value)
        )
    },
})

const priceFilterModel = computed({
    get: () => filters.value?.priceRange,
    set: (priceRange) => {
        filters.value = { ...filters.value, priceRange }
    },
})

const formattedPriceRange = useFormattedPriceRange(
    toRef(() => filters.value.priceRange)
)

const defaultSort: NonSkuGemstoneSortInput = {
    field: 'PRICE',
    direction: 'ASC',
}

const sorting = useObjectComputed(
    (): NonSkuGemstoneSortInput => hashParams.value?.sort || defaultSort
)

const sortingOptions: DropdownOption<NonSkuGemstoneSortInput>[] = [
    {
        value: {
            direction: NonSkuSortDirection.Asc,
            field: NonSkuGemstoneSortField.Price,
        },
        label: 'Price (low to high)',
    },
    {
        value: {
            direction: NonSkuSortDirection.Desc,
            field: NonSkuGemstoneSortField.Price,
        },
        label: 'Price (high to low)',
    },
    {
        value: {
            direction: NonSkuSortDirection.Asc,
            field: NonSkuGemstoneSortField.Carat,
        },
        label: 'Weight (low to high)',
    },
    {
        value: {
            direction: NonSkuSortDirection.Desc,
            field: NonSkuGemstoneSortField.Carat,
        },
        label: 'Weight (high to low)',
    },
]

const getSortRoute = (sorting: NonSkuGemstoneSortInput): ButtonRouteRelative =>
    routes.nonSku.gemstoneSearch({ ...filters.value }, sorting, stoneId.value)

const getRouteShape = (shape?: StoneShapeCode): ButtonRouteRelative =>
    routes.nonSku.gemstoneSearch(
        {
            ...filters.value,
            shape,
        },
        hashParams.value?.sort,
        stoneId.value
    )

const boundsInternal: Ref<NonSkuGemstoneBoundsFragment | null> = ref(null)
const { shapes, ringShape } = useRingShapeConstraintForNonSKU(
    useAllowedShapes(() => boundsInternal.value?.shapes)
)

if (!import.meta.env.SSR) {
    watch(
        [ringShape, hashParams],
        ([newValue, hashParams]) => {
            if (!newValue) {
                return
            }

            if (newValue.systemCode === hashParams?.filters?.shape) {
                return
            }

            const ringShapeRoute = getRouteShape(newValue.systemCode)

            if (ringShapeRoute.name !== router.currentRoute.value.name) {
                // Suspense causes you to remain on the old page so its watchers are still active
                // But the URL/current route have moved to the next route
                // The reason we end up here in the first place is hashParams also become empty
                return
            }

            const route = router.resolve(ringShapeRoute)
            redirect(route.fullPath, 302)
        },
        { immediate: true }
    )
}

const activeFilterCount = useFiltersCount(
    computed(() => [
        !!(
            filters.value?.caratWeightRange?.max ||
            filters.value?.caratWeightRange?.min
        ),
        !!(filters.value?.priceRange?.max || filters.value?.priceRange?.min),
        !ringShape.value && !!filters.value?.shape?.length,
        !!filters.value?.types?.length,
    ])
)

const filterChips = computed(() => {
    const chips: FilterChip[] = []

    if (formattedPriceRange.value) {
        chips.push({
            label: formattedPriceRange.value,
            onRemove: getClearFilter(filters, 'priceRange'),
        })
    }

    if (!ringShape.value && filters.value?.shape) {
        chips.push({
            label:
                shapes.value?.find((s) => s.systemCode === filters.value.shape)
                    ?.translation?.name ||
                systemCodeToWords(filters.value.shape),
            getRouteRemove: getRouteShape,
        })
    }

    if (
        filters.value.caratWeightRange?.min &&
        filters.value.caratWeightRange?.max
    ) {
        chips.push({
            label: `${t('diamond_search.filter.carat_size.chip', {
                min: formatNumericProperty(
                    Number(filters.value.caratWeightRange.min)
                ),
                max: formatNumericProperty(
                    Number(filters.value.caratWeightRange.max)
                ),
            })}`,
            onRemove: getClearFilter(filters, 'caratWeightRange'),
        })
    }

    if (filters.value.types) {
        filters.value.types.forEach((type) => {
            const a = boundsInternal.value?.types.find((t) => t.id === type)

            chips.push({
                label: t('diamond_search.filter.gemstone_type.chip', {
                    type: a && getGemstoneTypeName(a),
                }),
                onRemove: getClearFilter(filters, 'types', type),
            })
        })
    }

    return chips
})

const routeReset = computed(() => {
    return routes.nonSku.gemstoneSearch({
        shape: ringShape.value?.systemCode,
    })
})

const response = useGemstoneSearchQuery({ variables })

const stones = toRef(
    (): NonSkuGemstoneFragment[] | undefined => data.value?.nonSkuGemStones
)

const errors = useResponseError(response)

const { data, fetching, stale } = response

const prefetchPromise = response.then((res) => {
    boundsInternal.value = res.data.value?.nonSkuGemStonesBounds || null

    return res.data
})

useMeta(
    {
        title: () => data.value?.pageProductListing?.meta?.title || '',
        description: () =>
            data.value?.pageProductListing?.meta?.description || '',
    },
    data,
    fetching,
    stale
)

const getStoneRoute = (stoneId: string) =>
    routes.nonSku.gemstoneSearch(filters.value, hashParams.value?.sort, stoneId)

const { isActivated } = useActivated()

const closeStoneModal = () => {
    if (!isActivated.value) {
        return
    }

    router.replace(
        routes.nonSku.gemstoneSearch(
            filters.value,
            hashParams.value?.sort,
            undefined
        )
    )
}

const modalStone = computed((): NonSkuGemstoneFragment | undefined =>
    stones.value?.find((s) => s.id === stoneId.value)
)

const { routerPush } = useRouterNavigation()

const { ring } = useRingBuilder()
const selectStone = (v: NonSkuGemstoneFragment) => {
    if (ring.value) {
        routerPush(routes.ringBuilder.ring(ring.value, v))
    } else {
        routerPush(
            routes.engagementRings({
                shape: v.shape.systemCode,
            })
        )
    }
}

await prefetchPromise
</script>
